import React, { useRef, useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import "./TreeVine.css";

/* Vertical positions for each branch node */
const BRANCH_YS = [0.30, 0.50, 0.70];

/* Branch themes — unique shape, color, and character per section */
const BRANCH_THEMES = [
  {
    // Energy ⚡ — jagged lightning-bolt shapes, warm amber/gold accent
    color: "#F59E0B",
    glow: "#FDE68A",
    accent: "#D97706",
  },
  {
    // Robotics 🤖 — angular geometric lines, cool blue/violet accent
    color: "#818CF8",
    glow: "#C7D2FE",
    accent: "#6366F1",
  },
  {
    // Arctic ❄️ — soft crystalline fanning shapes, icy white/cyan accent
    color: "#67E8F9",
    glow: "#ECFEFF",
    accent: "#06B6D4",
  },
];

/* Twig positions — organic detail between branch points */
const TWIG_POSITIONS = [0.08, 0.15, 0.22, 0.38, 0.44, 0.58, 0.63, 0.80, 0.88, 0.94];

function TreeVineMobile({ progress }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { w, h } = dims;
  const cx = w / 2;

  /* ---- Trunk: big dramatic S-curves that use ~30% of screen width ---- */
  const trunkD = useMemo(() => {
    if (!w || !h) return "";
    const s = w * 0.15;
    return [
      `M ${cx} ${h * 0.02}`,
      `C ${cx + s * 0.1} ${h * 0.05} ${cx - s * 0.7} ${h * 0.09} ${cx - s * 0.6} ${h * 0.15}`,
      `C ${cx - s * 0.4} ${h * 0.21} ${cx + s * 0.6} ${h * 0.25} ${cx + s * 0.4} ${h * 0.285}`,
      `C ${cx + s * 0.2} ${h * 0.30} ${cx} ${h * 0.30} ${cx} ${h * 0.30}`,
      `C ${cx - s * 0.1} ${h * 0.31} ${cx + s * 0.8} ${h * 0.35} ${cx + s * 0.7} ${h * 0.40}`,
      `C ${cx + s * 0.5} ${h * 0.45} ${cx - s * 0.5} ${h * 0.47} ${cx - s * 0.3} ${h * 0.49}`,
      `C ${cx - s * 0.1} ${h * 0.498} ${cx} ${h * 0.50} ${cx} ${h * 0.50}`,
      `C ${cx + s * 0.1} ${h * 0.51} ${cx - s * 0.8} ${h * 0.55} ${cx - s * 0.7} ${h * 0.60}`,
      `C ${cx - s * 0.5} ${h * 0.65} ${cx + s * 0.5} ${h * 0.67} ${cx + s * 0.3} ${h * 0.69}`,
      `C ${cx + s * 0.1} ${h * 0.70} ${cx} ${h * 0.70} ${cx} ${h * 0.70}`,
      `C ${cx - s * 0.1} ${h * 0.71} ${cx + s * 0.7} ${h * 0.77} ${cx + s * 0.5} ${h * 0.84}`,
      `C ${cx + s * 0.2} ${h * 0.91} ${cx - s * 0.4} ${h * 0.95} ${cx} ${h * 0.98}`,
    ].join(" ");
  }, [cx, w, h]);

  /* ---- Unique branch shapes per section ---- */
  const branchShapes = useMemo(() => {
    if (!w || !h) return [];
    const reach = w * 0.30;

    // Energy ⚡ — jagged lightning bolt zig-zags
    const eY = BRANCH_YS[0] * h;
    const energyPaths = [
      // Left lightning bolt
      `M ${cx} ${eY} L ${cx - reach * 0.25} ${eY - h * 0.025} L ${cx - reach * 0.15} ${eY + h * 0.005} L ${cx - reach * 0.45} ${eY - h * 0.02} L ${cx - reach * 0.35} ${eY + h * 0.01} L ${cx - reach * 0.65} ${eY - h * 0.008}`,
      // Right lightning bolt
      `M ${cx} ${eY} L ${cx + reach * 0.25} ${eY - h * 0.02} L ${cx + reach * 0.18} ${eY + h * 0.008} L ${cx + reach * 0.5} ${eY - h * 0.015} L ${cx + reach * 0.4} ${eY + h * 0.012} L ${cx + reach * 0.7} ${eY - h * 0.005}`,
      // Left downward spark
      `M ${cx} ${eY} L ${cx - reach * 0.2} ${eY + h * 0.015} L ${cx - reach * 0.12} ${eY + h * 0.028} L ${cx - reach * 0.4} ${eY + h * 0.025}`,
      // Right downward spark
      `M ${cx} ${eY} L ${cx + reach * 0.22} ${eY + h * 0.018} L ${cx + reach * 0.14} ${eY + h * 0.03} L ${cx + reach * 0.42} ${eY + h * 0.022}`,
    ];

    // Robotics 🤖 — sharp geometric angles, circuit-like
    const rY = BRANCH_YS[1] * h;
    const rReach = reach * 0.9;
    const roboticsPaths = [
      // Left circuit arm — right-angle path
      `M ${cx} ${rY} L ${cx - rReach * 0.3} ${rY} L ${cx - rReach * 0.3} ${rY - h * 0.035} L ${cx - rReach * 0.6} ${rY - h * 0.035} L ${cx - rReach * 0.6} ${rY - h * 0.01}`,
      // Right circuit arm
      `M ${cx} ${rY} L ${cx + rReach * 0.3} ${rY} L ${cx + rReach * 0.3} ${rY - h * 0.03} L ${cx + rReach * 0.65} ${rY - h * 0.03} L ${cx + rReach * 0.65} ${rY + h * 0.005}`,
      // Left downward connector
      `M ${cx} ${rY} L ${cx - rReach * 0.2} ${rY} L ${cx - rReach * 0.2} ${rY + h * 0.03} L ${cx - rReach * 0.45} ${rY + h * 0.03}`,
      // Right downward connector
      `M ${cx} ${rY} L ${cx + rReach * 0.25} ${rY} L ${cx + rReach * 0.25} ${rY + h * 0.025} L ${cx + rReach * 0.5} ${rY + h * 0.025}`,
    ];

    // Arctic ❄️ — radiating crystal fan, soft curves
    const aY = BRANCH_YS[2] * h;
    const arcticPaths = [
      // Left upper fan
      `M ${cx} ${aY} C ${cx - reach * 0.2} ${aY - h * 0.05} ${cx - reach * 0.5} ${aY - h * 0.055} ${cx - reach * 0.7} ${aY - h * 0.04}`,
      // Left mid fan
      `M ${cx} ${aY} C ${cx - reach * 0.3} ${aY - h * 0.01} ${cx - reach * 0.6} ${aY - h * 0.005} ${cx - reach * 0.8} ${aY + h * 0.005}`,
      // Left lower fan
      `M ${cx} ${aY} C ${cx - reach * 0.2} ${aY + h * 0.04} ${cx - reach * 0.45} ${aY + h * 0.05} ${cx - reach * 0.65} ${aY + h * 0.04}`,
      // Right upper fan
      `M ${cx} ${aY} C ${cx + reach * 0.2} ${aY - h * 0.05} ${cx + reach * 0.5} ${aY - h * 0.055} ${cx + reach * 0.7} ${aY - h * 0.04}`,
      // Right mid fan
      `M ${cx} ${aY} C ${cx + reach * 0.3} ${aY - h * 0.01} ${cx + reach * 0.6} ${aY - h * 0.005} ${cx + reach * 0.8} ${aY + h * 0.005}`,
      // Right lower fan
      `M ${cx} ${aY} C ${cx + reach * 0.2} ${aY + h * 0.04} ${cx + reach * 0.45} ${aY + h * 0.05} ${cx + reach * 0.65} ${aY + h * 0.04}`,
    ];

    return [energyPaths, roboticsPaths, arcticPaths];
  }, [cx, w, h]);

  /* ---- Twigs — longer, more visible ---- */
  const twigDs = useMemo(() => {
    if (!w || !h) return [];
    return TWIG_POSITIONS.map((t, i) => {
      const y = (0.02 + t * 0.96) * h;
      const sign = i % 2 === 0 ? -1 : 1;
      const len = w * 0.06 + (i % 3) * w * 0.025;
      const endX = cx + sign * len;
      const endY = y - h * 0.018;
      const cpX = cx + sign * len * 0.55;
      const cpY = y - h * 0.012;
      return `M ${cx} ${y} Q ${cpX} ${cpY} ${endX} ${endY}`;
    });
  }, [cx, w, h]);

  /* ---- Branch growth synced to trunk progress ---- */
  const branchGrows = BRANCH_YS.map((by) => {
    const trunkArrives = (by - 0.02) / 0.96;
    const dur = 0.08;
    if (progress < trunkArrives) return 0;
    return Math.min((progress - trunkArrives) / dur, 1);
  });

  /* ---- Node glow opacity ---- */
  const nodeAlphas = branchGrows.map((g) =>
    g > 0.5 ? Math.min((g - 0.5) / 0.5, 1) : 0
  );

  /* ---- Growing tip position ---- */
  const trunkPathRef = useRef(null);
  const tipPos = useMemo(() => {
    if (!w || !h) return null;
    const pathEl = trunkPathRef.current;
    if (!pathEl) return { x: cx, y: (2 + progress * 96) / 100 * h };
    try {
      const totalLen = pathEl.getTotalLength();
      const pt = pathEl.getPointAtLength(totalLen * progress);
      return { x: pt.x, y: pt.y };
    } catch {
      return { x: cx, y: (2 + progress * 96) / 100 * h };
    }
  }, [progress, w, h, cx]);

  if (!w || !h) {
    return <div ref={containerRef} className="tree-vine-wrap" />;
  }

  return (
    <div ref={containerRef} className="tree-vine-wrap">
      <svg className="tree-vine-svg" width={w} height={h}>
        <defs>
          <linearGradient id="vineGradMobile" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#20B2AA" />
            <stop offset="50%" stopColor="#2EC4B6" />
            <stop offset="100%" stopColor="#40E0D0" />
          </linearGradient>
          {/* Per-branch radial glows */}
          <radialGradient id="nodeGlow0">
            <stop offset="0%" stopColor="#FDE68A" stopOpacity="1" />
            <stop offset="30%" stopColor="#F59E0B" stopOpacity="0.5" />
            <stop offset="70%" stopColor="#D97706" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#D97706" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="nodeGlow1">
            <stop offset="0%" stopColor="#C7D2FE" stopOpacity="1" />
            <stop offset="30%" stopColor="#818CF8" stopOpacity="0.5" />
            <stop offset="70%" stopColor="#6366F1" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="nodeGlow2">
            <stop offset="0%" stopColor="#ECFEFF" stopOpacity="1" />
            <stop offset="30%" stopColor="#67E8F9" stopOpacity="0.5" />
            <stop offset="70%" stopColor="#06B6D4" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
          </radialGradient>
          <filter id="vineBlurMobile">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* Trunk guide (also used for tip calc) */}
        <path ref={trunkPathRef} d={trunkD} stroke="rgba(32,178,170,0.06)"
          strokeWidth="3" fill="none" strokeLinecap="round" />

        {/* Trunk deep glow — wide and soft */}
        <motion.path d={trunkD} stroke="#20B2AA" strokeWidth="28"
          fill="none" strokeLinecap="round" opacity="0.06"
          filter="url(#vineBlurMobile)"
          style={{ pathLength: progress }} />

        {/* Trunk outer glow */}
        <motion.path d={trunkD} stroke="#20B2AA" strokeWidth="16"
          fill="none" strokeLinecap="round" opacity="0.10"
          style={{ pathLength: progress }} />

        {/* Trunk mid glow */}
        <motion.path d={trunkD} stroke="#2EC4B6" strokeWidth="8"
          fill="none" strokeLinecap="round" opacity="0.18"
          style={{ pathLength: progress }} />

        {/* Trunk core */}
        <motion.path d={trunkD} stroke="url(#vineGradMobile)" strokeWidth="4"
          fill="none" strokeLinecap="round"
          style={{ pathLength: progress }} />

        {/* Trunk bright highlight */}
        <motion.path d={trunkD} stroke="#A7FFEB" strokeWidth="1.5"
          fill="none" strokeLinecap="round" opacity="0.45"
          style={{ pathLength: progress }} />

        {/* Twig offshoots — bolder */}
        {twigDs.map((d, i) => {
          const twigT = 0.02 + TWIG_POSITIONS[i] * 0.96;
          const alpha = progress > twigT ? Math.min((progress - twigT) / 0.03, 1) : 0;
          return (
            <React.Fragment key={`tw-${i}`}>
              <motion.path d={d}
                stroke="#40E0D0" strokeWidth="6" fill="none" strokeLinecap="round"
                opacity={alpha * 0.08}
                style={{ pathLength: alpha }} />
              <motion.path d={d}
                stroke="#40E0D0" strokeWidth="2" fill="none" strokeLinecap="round"
                opacity={alpha * 0.5}
                style={{ pathLength: alpha }} />
              <motion.path d={d}
                stroke="#A7FFEB" strokeWidth="0.8" fill="none" strokeLinecap="round"
                opacity={alpha * 0.3}
                style={{ pathLength: alpha }} />
            </React.Fragment>
          );
        })}

        {/* Unique branch shapes — each with its own color and form */}
        {branchShapes.map((paths, i) => {
          const grow = branchGrows[i];
          if (grow <= 0) return null;
          const theme = BRANCH_THEMES[i];
          return paths.map((d, j) => (
            <React.Fragment key={`branch-${i}-${j}`}>
              {/* Deep glow */}
              <motion.path d={d} stroke={theme.color} strokeWidth="12"
                fill="none" strokeLinecap="round" strokeLinejoin="round"
                opacity={0.10 * grow}
                style={{ pathLength: grow }} />
              {/* Mid glow */}
              <motion.path d={d} stroke={theme.color} strokeWidth="6"
                fill="none" strokeLinecap="round" strokeLinejoin="round"
                opacity={0.20 * grow}
                style={{ pathLength: grow }} />
              {/* Core */}
              <motion.path d={d} stroke={theme.color} strokeWidth="2.5"
                fill="none" strokeLinecap="round" strokeLinejoin="round"
                opacity={0.6 * grow}
                style={{ pathLength: grow }} />
              {/* Highlight */}
              <motion.path d={d} stroke={theme.glow} strokeWidth="1"
                fill="none" strokeLinecap="round" strokeLinejoin="round"
                opacity={0.4 * grow}
                style={{ pathLength: grow }} />
            </React.Fragment>
          ));
        })}

        {/* Themed glow nodes at branch points */}
        {BRANCH_YS.map((by, i) => {
          const a = nodeAlphas[i];
          if (a <= 0) return null;
          const y = by * h;
          const theme = BRANCH_THEMES[i];
          return (
            <g key={`node-${i}`} opacity={a}>
              <circle cx={cx} cy={y} r="40" fill={`url(#nodeGlow${i})`} opacity="0.5" />
              <circle cx={cx} cy={y} r="20" fill={`url(#nodeGlow${i})`} />
              <circle cx={cx} cy={y} r="8" fill={theme.accent} opacity="0.15" />
              <circle cx={cx} cy={y} r="5" fill={theme.color} opacity="0.4" />
              <circle cx={cx} cy={y} r="3" fill={theme.color} opacity="0.7" />
              <circle cx={cx} cy={y} r="1.5" fill={theme.glow} opacity="1" />
            </g>
          );
        })}

        {/* Decorative dots along trunk — larger */}
        {[0.06, 0.18, 0.36, 0.55, 0.75, 0.85, 0.93].map((t, i) => {
          const dotY = (0.02 + t * 0.96) * h;
          const visible = progress > 0.02 + t * 0.96;
          return visible ? (
            <g key={`dot-${i}`}>
              <circle cx={cx} cy={dotY} r="4" fill="#20B2AA" opacity="0.12" />
              <circle cx={cx} cy={dotY} r="2.5" fill="#20B2AA" opacity="0.25" />
              <circle cx={cx} cy={dotY} r="1.2" fill="#40E0D0" opacity="0.5" />
            </g>
          ) : null;
        })}
      </svg>

      {/* Growing tip — larger and brighter */}
      {tipPos && (
        <div className="tree-vine-tip tree-vine-tip--mobile"
          style={{
            left: `${tipPos.x}px`,
            top: `${tipPos.y}px`,
            opacity: progress > 0.01 && progress < 0.97 ? 1 : 0,
          }}
        />
      )}
    </div>
  );
}

export default TreeVineMobile;
